import {
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  OnInit,
  output,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  firstValueFrom,
  map,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
  timer,
  interval,
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import moment from 'moment';
import _ from 'lodash';
import { MessageService } from '../../../../../../../services/message.service';
import {
  IntegrationService,
} from '../../../../../../../services/integration.service';
import {
  DiscoveredUrl,
  DiscoveredUrlsFilters,
  DiscoveredUrlsByDepthData,
  DiscoveredUrlStatus,
} from '../../../../../../../models/discoveredUrls';
import {
  Option,
} from '../../../../../../../components/select/select.component';
import {
  Crawler,
  CrawlerLaunchBlockReason,
  CrawlerLaunchEligibility,
  CrawlerStatus,
  CrawlerType,
  DiscoveryCrawlerSpec,
  MAX_DISCOVERY_DEPTH,
} from '../../../../../../../models/integration/crawler';
import {
  ConfirmationDialogComponent,
} from '../../../../../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  DiscoveredUrlsService,
} from '../../../../../../../services/discovered-urls.service';
import {
  PaginatedListManager,
} from '../../../../../../../shared/single-crawler/paginated-list.manager';
import {
  LaunchEligibilityPoller,
  LaunchPending,
  POLL_INTERVAL_MS,
  isCrawlerActive,
} from '../../../../../../../shared/single-crawler/launch-eligibility';
import { UtilsService } from '../../../../../../../services/utils.service';
import { OrganizationService } from '../../../../../../../services/organization.service';

// Discovery type integrations only. List of all discovered backlinks
@Component({
  selector: 'app-single-crawler-discovery-step',
  templateUrl: './single-crawler-discovery-step.component.html',
  styleUrls: ['./single-crawler-discovery-step.component.scss'],
})
export class SingleCrawlerDiscoveryStepComponent implements OnInit {
  readonly integrationId = input.required<string>();
  readonly eligibilityPoller = input.required<LaunchEligibilityPoller>();
  readonly crawler = input.required<Crawler>();
  readonly discoveryLaunched = output<void>();
  readonly selectionChange = output<DiscoveredUrl[]>();

  protected readonly DiscoveredUrlStatus = DiscoveredUrlStatus;
  protected readonly pageSizeOptions = [10, 25, 50, 100];

  selection = new SelectionModel<DiscoveredUrl>(
    true,
    [],
  );
  launching = false;

  // Single poll shared with the container: it feeds the discover/cancel
  // buttons and the backlinks table auto-refresh without a second HTTP poll.
  get eligibility(): CrawlerLaunchEligibility | undefined {
    return this.eligibilityPoller().eligibility;
  }

  get launchPending(): LaunchPending {
    return this.eligibilityPoller().launchPending;
  }

  // Labelled by distance from the start page, filtering on the stored depth.
  readonly depthSelectOptions = computed<Option[]>(() => {
    const requested = this.requestedDepth();
    return Array.from(
      { length: (requested ?? MAX_DISCOVERY_DEPTH) + 1 }, // include the start page
      (_v, distance) => ({
        label: distance.toString(10),
        value: (requested === undefined ? distance : requested - distance).toString(10),
        selected: false,
        disabled: false,
      }),
    );
  });

  private readonly columns: string[] = [
    'select',
    'url',
    'status',
    'depth',
    'createdAt',
  ];

  // Once a capture is launched the pages are what it captures: nothing to select any more.
  get displayedColumns(): string[] {
    return this.canEditCaptureSelection() ? this.columns : this.columns.filter((column) => column !== 'select');
  }

  readonly list: PaginatedListManager<DiscoveredUrl, DiscoveredUrlsFilters>;

  private readonly active$: Observable<boolean>
  private readonly graphData$: Observable<never[] | DiscoveredUrlsByDepthData[]>;
  private readonly tick$: Observable<number>;

  graphData: DiscoveredUrlsByDepthData[] = [];
  loadingGraphData = true;

  constructor(
    private readonly _message: MessageService,
    private readonly dialog: MatDialog,
    private readonly discoveredUrlsService: DiscoveredUrlsService,
    private readonly integrationService: IntegrationService,
    private readonly organizationService: OrganizationService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly utils: UtilsService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.list = new PaginatedListManager<DiscoveredUrl, DiscoveredUrlsFilters>({
      destroyRef,
      initialFilters: {
        query: undefined,
        depth: undefined,
      },
      retrieveFn: (page, limit, offset, sort, filters) =>
        this.discoveredUrlsService.retrieveBacklinksPaginated(
          this.integrationId(),
          page,
          limit,
          offset,
          sort,
          filters,
        ),
      onError: (err) => this.utils.toastError(err),
      onLoadingStart: () => this._message.emitChange(
        'LOADING',
        'START',
      ),
      onLoadingEnd: () => this._message.emitChange(
        'LOADING',
        'END',
      ),
    });

    // push the eligibilityPoller() read to subscription time (when available)
    this.active$ = defer(() => this.eligibilityPoller().eligibility$).pipe(
      map((eligibility) => isCrawlerActive(eligibility.status)),
    );

    // one shared ticker with replay of the latest tick only (for combineLatest of graphData$)
    // distinctUntilChanged: while active, every poll re-emits true; without it
    // switchMap would cancel + restart the interval every time
    this.tick$ = this.active$.pipe(
      distinctUntilChanged(),
      switchMap(active =>
        active
          ? interval(POLL_INTERVAL_MS)
          : timer(0) // Emit once then complete (final fetch)
      ),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

    // get graph data on initial load + when ticker is active (discovery in progress)
    this.graphData$ = combineLatest([
      toObservable(this.integrationId),
      this.tick$,
    ])
      .pipe(
        switchMap(([id]) =>
          from(this.integrationService.retrieveDiscoveredUrlsByDepth(id))
            .pipe(
              catchError(() => {
                this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
                return of([]);
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      );

    effect(() => {
      const integrationId = this.integrationId();
      if (integrationId) {
        void this.list.init();
        this.list.startAutoRefresh(this.active$);
      }
    });
  }

  ngOnInit(): void {
    // need active$ subscription to be available
    this.graphData$.subscribe((data) => {
      this.graphData = data.map(({ depth, count }) => ({ depth: this.distance(depth), count }));
      this.loadingGraphData = false;
    });
  }

  async handlePage(event: PageEvent): Promise<void> {
    await this.list.handlePage(event);
  }

  async onSortChange(event: Sort): Promise<void> {
    await this.list.onSortChange(event);
  }

  onQueryChange(query?: string): void {
    this.list.updateFilters({ query });
  }

  onDepthChange(event?: { value: Option }): void {
    this.list.updateFilters({ depth: event?.value?.value });
  }

  /**
   * The orchestrator stores the depth left to explore: the start page holds the requested depth and each
   * level of links one less. Users read the distance from the start page instead (0 for the start page).
   */
  distance(depth: number): number {
    const requested = this.requestedDepth();
    return requested === undefined ? depth : Math.max(requested - depth, 0);
  }

  private requestedDepth(): number | undefined {
    const crawler = this.crawler();
    return crawler.type === CrawlerType.DISCOVERY ? crawler.spec.discoveryDepth : undefined;
  }

  createdAt(discoveredUrl: DiscoveredUrl): string {
    return discoveredUrl.createdAt
      ? moment(discoveredUrl.createdAt).format('YYYY-MM-DD HH:mm')
      : '-';
  }

  // Why a page could not be explored, in words: the start page of an unreachable site, mostly.
  errorReason(discoveredUrl: DiscoveredUrl): string {
    return discoveredUrl.errorMessage
      ? `INTEGRATIONS.BACKLINK_CRAWLER.ERRORS.${discoveredUrl.errorMessage}`
      : 'INTEGRATIONS.BACKLINK_CRAWLER.ERRORS.UNKNOWN';
  }

  // Pages found, then a relaunch possible: the exploration can start again from its start page.
  canRediscover(): boolean {
    return !!this.eligibility
      && this.eligibility.discoveryBlockReason === CrawlerLaunchBlockReason.URLS_ALREADY_DISCOVERED
      && this.canEditCaptureSelection() && !this.launching;
  }

  // Deletes every page found and explores again. The pages found stay billed, the new run books its ceiling.
  async rediscover(): Promise<void> {
    const quotas = await this.organizationService.myQuotas().catch(() => undefined);
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'INTEGRATIONS.BACKLINK_CRAWLER.REDISCOVER_TITLE',
        text: this.translate.instant('INTEGRATIONS.BACKLINK_CRAWLER.REDISCOVER_TEXT', {
          found: Math.max(0, this.list.paginator.totalDocs - 1),
          ceiling: (this.crawler().spec as DiscoveryCrawlerSpec).maxDiscoverablePages,
          available: quotas?.crawlerUrls?.available ?? '-',
        }),
        button: { text: 'INTEGRATIONS.BACKLINK_CRAWLER.REDISCOVER', class: 'main-button' },
      },
    };
    const result: { confirmed: boolean } = await firstValueFrom(
      this.dialog.open(ConfirmationDialogComponent, config).afterClosed(),
    );
    if (!result?.confirmed) return;

    try {
      this.launching = true;
      this.launchPending.mark();
      await this.integrationService.rediscover(this.integrationId());
      this.toastr.success(this.translate.instant('INTEGRATIONS.DISCOVERY.LAUNCHED'));
      this.selection.clear();
      this.selectionChange.emit(this.selection.selected);
      this.discoveryLaunched.emit();
      await this.list.retrieve();
      this.eligibilityPoller().start(this.integrationId());
    } catch (err: unknown) {
      this.launchPending.clear();
      this.utils.toastError(err);
    } finally {
      this.launching = false;
    }
  }

  // The orchestrator reads the capture selection once, when the crawl starts.
  canEditCaptureSelection(): boolean {
    return !!this.eligibility && this.eligibility.crawlBlockReason !== CrawlerLaunchBlockReason.CRAWL_ALREADY_RUN
      && !this.eligibility.isDiscoveryRunning;
  }

  async setSelectionExcluded(excluded: boolean): Promise<void> {
    try {
      this.list.loading = true;
      this._message.emitChange('LOADING', 'START');
      await this.discoveredUrlsService.setExcluded(
        this.integrationId(),
        this.selection.selected.map((doc) => doc._id!),
        excluded,
      );
      this.toastr.success(this.translate.instant('INTEGRATIONS.BACKLINK_CRAWLER.SELECTION_UPDATED'));
      this.selection.clear();
      this.selectionChange.emit(this.selection.selected);
      await this.list.retrieve();
      // the crawl button shows how many pages it will capture
      this.eligibilityPoller().start(this.integrationId());
    } catch (err: unknown) {
      this.utils.toastError(err);
    } finally {
      this.list.loading = false;
      this._message.emitChange('LOADING', 'END');
    }
  }

  // selection
  hasPageSelection(): boolean {
    return this.list.paginator.docs.some((row) => this.isSelected(row));
  }

  isSelected(row: DiscoveredUrl): boolean {
    return this.selection.selected.map((doc) => doc._id)
      .includes(row._id);
  }

  isAllSelected(): boolean {
    return this.list.paginator.docs.every((row) => this.isSelected(row));
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      const difference = _.differenceBy(
        this.selection.selected,
        this.list.paginator.docs,
        '_id',
      );
      this.selection.setSelection(...difference);
      this.selectionChange.emit(this.selection.selected);
      return;
    }
    this.selection.setSelection(...[
      ...this.selection.selected,
      ...this.list.paginator.docs.filter((row) => !this.isSelected(row)),
    ]);
    this.selectionChange.emit(this.selection.selected);
  }

  toggle(row: DiscoveredUrl): void {
    if (this.isSelected(row)) {
      this.selection.setSelection(...this.selection.selected.filter((doc) => doc._id !== row._id));
    } else {
      this.selection.setSelection(...[...this.selection.selected, row]);
    }
    this.selectionChange.emit(this.selection.selected);
  }

  async launchDiscovery(): Promise<void> {
    try {
      this.launching = true;
      this.launchPending.mark();
      await this.integrationService.trigger(
        this.integrationId(),
        false,
        'discover',
      );
      this.toastr.success(this.translate.instant('INTEGRATIONS.DISCOVERY.LAUNCHED'));
      this.discoveryLaunched.emit();
      await this.list.retrieve();
    } catch (err: unknown) {
      this.launchPending.clear();
      this.utils.toastError(err);
    } finally {
      this.launching = false;
    }
  }

  async cancelDiscovery(): Promise<void> {
    try {
      await this.integrationService.cancelDiscovery(
        this.integrationId(),
      );
      this.toastr.success(this.translate.instant('INTEGRATIONS.CANCEL_CRAWLER_SUCCESS'));
      this.launchPending.clear();
    } catch (err: unknown) {
      this.utils.toastError(err);
    }
  }

  canRunDiscovery(): boolean {
    return !!this.eligibility?.canRunDiscovery && !this.launching;
  }

  protected isDiscovering() {
    return this.launching || this.eligibility?.status === CrawlerStatus.DISCOVERING;
  }
}
